import { IcDeIcing, IcGc, IcHeating, IcMwir, IcPc, IcSwir, IcTurret } from '@assets/icons/32x32';
import { chakra } from '@chakra-ui/react';
import { colors } from '@theme/foundations/colors';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { HealthStatusEnum } from '@/types/sensor/sensors';

import Gate from '../../schematic/Gate';
import SquareInfo from '../../schematic/SquareInfo';
import { commonHolopticsI18nPath } from './HolopticsSchematic';

const Text = chakra('text');

export default function HolopticsCameraSchematic() {
  const { formatMessage } = useIntl();
  const textRef = useRef<SVGTextElement | null>(null);
  const [bbox, setBbox] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (textRef.current) {
      const box = textRef.current.getBBox();
      setBbox(box);
    }
  }, []);

  return (
    <svg width="1219" height="519" viewBox="0 0 1219 519" fill="none">
      {bbox && (
        <rect
          x={bbox.x - 8}
          y={bbox.y - 3}
          width={bbox.width + 16}
          height={bbox.height + 7}
          fill={colors.neutral['600'].value}
        />
      )}
      <Text
        ref={textRef}
        y="16"
        x="1214"
        textAnchor="end"
        fontWeight="medium"
        fill={colors.neutral['300'].value}
        style={{ textTransform: 'uppercase' }}
      >
        {formatMessage({ id: `${commonHolopticsI18nPath}.camera` })}
      </Text>
      <rect width="463" height="494" transform="translate(756 25)" fill={colors.neutral['600'].value} />
      <rect width="238" height="266" transform="translate(957 41)" fill={colors.neutral['750'].value} />
      <rect width="168" height="86" transform="translate(1011 57)" fill={colors.neutral['600'].value} />
      <rect width="168" height="86" transform="translate(1011 151)" fill={colors.neutral['600'].value} />
      <rect width="232" height="102" transform="translate(957 315)" fill={colors.neutral['750'].value} />
      <rect width="72" height="78" transform="translate(957 425)" fill={colors.neutral['750'].value} />

      {/* Turret */}
      <SquareInfo
        x={780}
        y={249}
        name={formatMessage({ id: `${commonHolopticsI18nPath}.turret` })}
        isConnected
        isPresent
        icon={IcTurret}
        status={HealthStatusEnum.HEALTHY}
      />

      {/* GC and PC */}
      <Gate x={871.5} y={51.5} isConnected orientation="horizontal" onClick={() => console.log('click')} />
      <SquareInfo
        x={1051}
        y={81}
        name={formatMessage({ id: `${commonHolopticsI18nPath}.gc` })}
        isConnected
        isPresent
        icon={IcGc}
        status={HealthStatusEnum.HEALTHY}
      />
      <SquareInfo
        x={1123}
        y={81}
        name={formatMessage({ id: `${commonHolopticsI18nPath}.deIcing` })}
        isConnected
        isPresent
        icon={IcDeIcing}
        status={HealthStatusEnum.HEALTHY}
      />
      <SquareInfo
        x={1051}
        y={175}
        name={formatMessage({ id: `${commonHolopticsI18nPath}.pc` })}
        isConnected
        isPresent
        icon={IcPc}
        status={HealthStatusEnum.HEALTHY}
      />
      <SquareInfo
        x={1123}
        y={175}
        name={formatMessage({ id: `${commonHolopticsI18nPath}.deIcing` })}
        isConnected
        isPresent
        icon={IcDeIcing}
        status={HealthStatusEnum.HEALTHY}
      />
      <SquareInfo
        x={1123}
        y={245}
        name={formatMessage({ id: `${commonHolopticsI18nPath}.heating` })}
        isConnected
        isPresent
        icon={IcHeating}
        status={HealthStatusEnum.HEALTHY}
      />
      <path
        name="GC link"
        d="M1047.5 104C1041.98 104 1037.5 99.5228 1037.5 94V86C1037.5 76.0589 1029.44 68 1019.5 68H904.5"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
        //display={isPresent ? 'block' : 'none'}
      />
      <path
        name="GC deIcing link"
        d="M1119.5 104C1113.98 104 1109.5 99.5228 1109.5 94V86C1109.5 76.0589 1101.44 68 1091.5 68H904.5"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
        //display={isPresent ? 'block' : 'none'}
      />
      <path
        name="PC link"
        d="M1047.5 198C1040.87 198 1035.5 192.627 1035.5 186V181.5C1035.5 172.387 1028.11 165 1019 165H1011.5C998.245 165 987.5 154.255 987.5 141V92C987.5 78.7452 976.755 68 963.5 68H904.5"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
        //display={isPresent ? 'block' : 'none'}
      />
      <path
        name="PC deIcing link"
        d="M1119.5 198H1117C1107.89 198 1100.5 190.613 1100.5 181.5C1100.5 172.387 1093.11 165 1084 165H1011.5C998.245 165 987.5 154.255 987.5 141V92C987.5 78.7452 976.755 68 963.5 68H904.5"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
        //display={isPresent ? 'block' : 'none'}
      />
      <path
        name="GC and PC heating link"
        d="M1119.5 268H1011.5C998.245 268 987.5 257.255 987.5 244V92C987.5 78.7452 976.755 68 963.5 68H904.5"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
        //display={isPresent ? 'block' : 'none'}
      />

      {/* SWIR */}
      <Gate x={880} y={311.5} isConnected orientation="horizontal" />
      <SquareInfo
        x={973}
        y={351}
        name={formatMessage({ id: `${commonHolopticsI18nPath}.swir` })}
        isConnected
        isPresent
        icon={IcSwir}
        status={HealthStatusEnum.HEALTHY}
      />
      <SquareInfo
        x={1053}
        y={351}
        name={formatMessage({ id: `${commonHolopticsI18nPath}.deIcing` })}
        isConnected
        isPresent
        icon={IcDeIcing}
        status={HealthStatusEnum.HEALTHY}
      />
      <SquareInfo
        x={1133}
        y={351}
        name={formatMessage({ id: `${commonHolopticsI18nPath}.heating` })}
        isConnected
        isPresent
        icon={IcHeating}
        status={HealthStatusEnum.HEALTHY}
      />
      <path
        name="Swir link"
        d="M993 347.5C993 336.73 984.27 328 973.5 328H913"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
        //display={isPresent ? 'block' : 'none'}
      />
      <path
        name="Swir deicing link"
        d="M1073 347.5C1073 336.73 1064.27 328 1053.5 328H913"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        name="Swir heating link"
        d="M1153 347.5C1153 336.73 1144.27 328 1133.5 328H913"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
        //display={isPresent ? 'block' : 'none'}
      />

      {/* MWIR */}
      <Gate x={877} y={447} isConnected orientation="horizontal" />
      <SquareInfo
        x={973}
        y={441}
        name={formatMessage({ id: `${commonHolopticsI18nPath}.mwir` })}
        isConnected
        isPresent
        icon={IcMwir}
        status={HealthStatusEnum.HEALTHY}
      />
      <path d="M969.5 463.5H910" stroke={colors.neutral['200'].value} strokeWidth="2" strokeLinejoin="bevel" />

      {/*  Other Links  */}
      <path
        d="M878 463.5H866C852.745 463.5 842 452.755 842 439.5V290.5C842 280.283 833.717 272 823.5 272"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        d="M881 328H876.25C862.995 328 852.25 317.255 852.25 304V296C852.25 282.745 841.505 272 828.25 272H823.5"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        d="M872.5 68H867C853.745 68 843 78.7452 843 92V252.5C843 263.27 834.27 272 823.5 272"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        d="M666 285V298C666 311.255 676.745 322 690 322H705C718.255 322 729 311.255 729 298V296C729 282.745 739.745 272 753 272H776.5"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
    </svg>
  );
}
